import { AsyncPipe, DecimalPipe } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FormHelper,
  OnyxFormGroupComponent,
  OnyxLanguagePipe,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { combineLatestWith, distinctUntilChanged, map } from 'rxjs';
import type { OrderLoadingPointGoodForm } from '../../../../../common/helpers/order-form.helper';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import { OrderFormService } from '../../../../../common/services/order-form.service';
import { availableGoodQuantityValidator } from '../../../../../common/validators/unloading-good-quantity.validator';

export type OrderUnloadingPointGoodFormGroup = ReturnType<
  typeof OrderUnloadingPointGoodFormComponent.buildForm
>;

@Component({
  selector: 'app-order-unloading-point-good-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    OnyxFormGroupComponent,
    OnyxTextFieldComponent,
    DecimalPipe,
    AsyncPipe,
  ],
  templateUrl: './order-unloading-point-good-form.component.html',
  styleUrl: './order-unloading-point-good-form.component.scss',
})
export class OrderUnloadingPointGoodFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderFormService = inject(OrderFormService);

  public readonly form = input.required<OrderUnloadingPointGoodFormGroup>();
  public readonly good = input.required<OrderLoadingPointGoodForm>();
  public readonly availableQuantity = input.required<number>();

  protected readonly I18N = 'orders.orderForm.points.unloadingPoint.good';

  protected readonly Math = Math;

  private readonly units$ = this.orderFormService.units$;

  protected unitName$ = toObservable(this.good).pipe(
    map((good) => good.unit),
    distinctUntilChanged(),
    combineLatestWith(this.units$),
    map(([goodUnit, units]) => {
      if (!goodUnit) return null;

      const unit = units.find((unit) => unit.uuid === goodUnit);
      return unit ? this.languagePipe.transform(unit.names) : null;
    }),
    takeUntilDestroyed(this.destroyRef),
  );

  protected unit = computed(() =>
    OrderHelper.getGoodUnit(this.good().size.type),
  );
  protected decimalPlaces = computed(() =>
    OrderHelper.getGoodSizeDecimalPlaces(this.good()?.size?.type),
  );

  protected unloadedWeight = computed(() => {
    const [quantity, good] = [this.quantity(), this.good()];
    if (!quantity || !good?.totalWeight) return null;

    const loadingQuantity = OrderHelper.getGoodQuantity(good);
    if (!loadingQuantity) return null;

    return (quantity / loadingQuantity) * good.totalWeight;
  });

  private quantity!: Signal<number | null>;

  public ngOnInit() {
    const controls = this.form().controls;
    effect(
      () => {
        controls.quantity.setValidators([
          Validators.required,
          Validators.min(1),
          availableGoodQuantityValidator(
            this.availableQuantity() / 10 ** this.decimalPlaces(),
          ),
        ]);
        if (controls.quantity.value) FormHelper.submit(controls.quantity);
      },
      { injector: this.injector },
    );

    this.quantity = toSignal(controls.quantity.valueChanges, {
      initialValue: controls.quantity.value,
      injector: this.injector,
    });
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    uuid: string,
    quantity: number,
  ) {
    return fb.group({
      uuid: fb.control(uuid),
      quantity: fb.control<number | null>(quantity, [
        Validators.required,
        Validators.min(1),
      ]),
    });
  }
}
